import React, { useEffect, useState } from 'react';
import { Box, Chip, Container, IconButton, Menu, MenuItem, Paper, Tooltip, Typography } from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import EmailIcon from '@mui/icons-material/Email';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import dayjs from 'dayjs';

import { AutoCompleteSelectUI, DataGridIdentifiers, DatagridPremiumUi } from '../../../Interface';
import dynamicFormService from '../../../../service/dynamicForm.service';
import addNotification from '../../../../utils/addNotification';
import { NotificationType } from '../../../../config';
import colorFinder from '../../../../utils/colorFinder';
import WorksheetEmailDialog from '../Worksheet/WorksheetEmailDialog';
import useConfig from '../../../../context/ConfigContext';
import useUsers from '../../../../context/UsersContext';

const WorksheetReport = () => {
  const { config, getConfig } = useConfig();
  const { user, getUser } = useUsers();
  const apiRef = useGridApiRef();
  const [worksheetOptions, setWorksheetOptions] = useState([]);
  const [selectedWorksheetId, setSelectedWorksheetId] = useState(null);
  const [report, setReport] = useState({ columns: [], rows: [] });
  const [loading, setLoading] = useState(false);
  const [statusOptions, setStatusOptions] = useState([]);
  const [worksheetEmailEnabled, setWorksheetEmailEnabled] = useState(false);
  const [statusMenu, setStatusMenu] = useState({ anchorEl: null, row: null });
  const [emailDialog, setEmailDialog] = useState({
    open: false,
    headId: null,
    templates: [],
    fixedAttachments: [],
    initialEmailValues: null,
  });

  useEffect(() => {
    if (!Object.keys(config).length) getConfig();
    if (!Object.keys(user).length) getUser();
  }, [config, getConfig, getUser, user]);

  useEffect(() => {
    dynamicFormService.getWorksheetReportForms().then((data) => {
      setWorksheetOptions(
        (data || []).map((worksheet) => ({
          label: worksheet.name,
          value: worksheet.id,
        }))
      );
    });
  }, []);

  useEffect(() => {
    dynamicFormService.getWorksheetStatuses().then((data) => {
      setStatusOptions(data || []);
    });
    dynamicFormService.getWorksheetEmailOptions().then((data) => {
      setWorksheetEmailEnabled(Boolean(data?.enabled));
    });
  }, []);

  const handleOpenPdf = async (headId) => {
    try {
      const pdf = await dynamicFormService.getWorksheetPdf(headId);
      const url = URL.createObjectURL(pdf);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
      const responseType = error?.response?.data?.type;
      if (error?.response?.status === 404 && responseType !== 'application/json') {
        addNotification({ content: 'Munkalap nem elérhető.', type: NotificationType.ERROR });
      }
    }
  };

  const handleStatusChange = async (statusId) => {
    if (!statusMenu.row) return;

    try {
      const updated = await dynamicFormService.updateWorksheetStatus(statusMenu.row.id, statusId);
      setReport((currentReport) => ({
        ...currentReport,
        rows: currentReport.rows.map((currentRow) =>
          currentRow.id === updated.id ? { ...currentRow, statusId: updated.statusId, status: updated.status } : currentRow
        ),
      }));
      addNotification({ content: 'A munkalap státusza frissült.', type: NotificationType.SUCCESS });
    } catch (_error) {
      // A központi request interceptor jeleníti meg a backend hibaüzenetét.
    } finally {
      setStatusMenu({ anchorEl: null, row: null });
    }
  };

  const handleOpenEmailDialog = async (row) => {
    const options = await dynamicFormService.getWorksheetEmailOptions(row.projectId, row.dynamicFormId);
    if (!options?.enabled) {
      addNotification({ content: 'A munkalap e-mail küldése nincs engedélyezve.', type: NotificationType.WARNING });
      return;
    }
    if (!options?.templates?.length) {
      addNotification({ content: 'Nem érhető el munkalap e-mail sablon.', type: NotificationType.WARNING });
      return;
    }

    setEmailDialog({
      open: true,
      headId: row.id,
      templates: options.templates || [],
      fixedAttachments: row.worksheetAttachmentName ? [{ name: row.worksheetAttachmentName }] : [],
      initialEmailValues: { to: row.worksheetEmailRecipients || options.worksheetEmailRecipients || '', templateId: options.worksheetEmailTemplateId },
    });
  };

  const handleSendWorksheetEmail = async (emailData) => {
    const { files = [], ...data } = emailData;
    const result = await dynamicFormService.sendWorksheetEmail(emailDialog.headId, data, files);
    setReport((currentReport) => ({
      ...currentReport,
      rows: currentReport.rows.map((row) =>
        row.id === emailDialog.headId
          ? {
              ...row,
              sendWorksheetEmail: result?.head?.sendWorksheetEmail ?? true,
              worksheetEmailRecipients: result?.head?.worksheetEmailRecipients ?? row.worksheetEmailRecipients,
              worksheetEmailSentBy: result?.head?.worksheetEmailSentBy ?? row.worksheetEmailSentBy,
              worksheetEmailSentAt: result?.head?.worksheetEmailSentAt ? new Date(result.head.worksheetEmailSentAt) : row.worksheetEmailSentAt,
            }
          : row
      ),
    }));
    addNotification({ content: 'A munkalap kiküldése sikeres!', type: NotificationType.SUCCESS });
    setEmailDialog((current) => ({ ...current, open: false }));
  };

  const handleWorksheetChange = (_event, selectedOption) => {
    const worksheetId = selectedOption?.value ?? null;
    setSelectedWorksheetId(worksheetId);
    setReport({ columns: [], rows: [] });

    if (!worksheetId) return;

    setLoading(true);
    dynamicFormService
      .getWorksheetReport(worksheetId)
      .then((data) => {
        setReport({
          columns: (data?.columns || []).map((column) =>
            column.field === 'createdDate'
              ? {
                  ...column,
                  renderCell: (params) =>
                    params.row.createdDate ? dayjs(params.row.createdDate).format('YYYY.MM.DD.') : '',
                }
              : column.field === 'worksheetEmailSentAt'
                ? {
                    ...column,
                    renderCell: (params) => params.value ? dayjs(params.value).format('YYYY.MM.DD. HH:mm:ss') : '',
                  }
                : column
          ),
          rows: (data?.rows || []).map((row) => ({
            ...row,
            createdDate: row.createdDate ? new Date(`${row.createdDate}T00:00:00`) : null,
            worksheetEmailSentAt: row.worksheetEmailSentAt ? new Date(row.worksheetEmailSentAt) : null,
          })),
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const columns = [
    {
      field: 'pdf',
      headerName: 'PDF',
      width: 70,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Tooltip title="Munkalap megnyitása">
          <IconButton color="error" size="small" onClick={() => handleOpenPdf(params.row.id)}>
            <PictureAsPdfIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
    {
      field: 'statusId',
      headerName: 'Státusz',
      type: 'singleSelect',
      width: 170,
      valueOptions: statusOptions.map((status) => ({ value: status.itemId, label: status.itemName })),
      renderCell: (params) => {
        const status = params.row.status;
        const statusColor = colorFinder(status?.color);

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Chip
              label={status?.itemName || 'Nincs státusz'}
              size="small"
              variant="outlined"
              clickable
              onClick={(event) => {
                event.stopPropagation();
                setStatusMenu({ anchorEl: event.currentTarget, row: params.row });
              }}
              sx={{
                color: statusColor?.textColor,
                borderColor: statusColor?.colorCode,
                backgroundColor: statusColor?.kanbanChipBgColor,
              }}
            />
          </Box>
        );
      },
    },
    ...(worksheetEmailEnabled
      ? [
          {
            field: 'email',
            headerName: 'E-mail',
            width: 80,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params) => (
              <Tooltip title="Munkalap küldése e-mailben">
                <IconButton color="primary" size="small" onClick={() => handleOpenEmailDialog(params.row)}>
                  <EmailIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ),
          },
        ]
      : []),
    ...report.columns,
  ];

  return (
    <Container maxWidth={false} sx={{ py: 2 }}>
      <Typography variant="h5" mb={2}>
        Munkalapok
      </Typography>

      <Paper elevation={1} sx={{ p: 2 }}>
        <Box maxWidth={500} mb={2}>
          <AutoCompleteSelectUI
            label="Munkalap fajtája"
            options={worksheetOptions}
            value={worksheetOptions.find((option) => option.value === selectedWorksheetId) || null}
            onChange={handleWorksheetChange}
            placeholder="Válassz munkalapfajtát"
          />
        </Box>

        <Box sx={{ height: 'calc(100vh - 210px)', minHeight: 400, width: '100%' }}>
          <DatagridPremiumUi
            apiRef={apiRef}
            datagridIdentifier={DataGridIdentifiers.WorksheetReport}
            rows={report.rows}
            columns={columns}
            loading={loading}
            pagination={false}
            paginationAndfilterMode="client"
            disableSave
            height="100%"
            hideFooter
            headerFilters
            getRowHeight={() => 'auto'}
          />
        </Box>
      </Paper>

      <Menu
        anchorEl={statusMenu.anchorEl}
        open={Boolean(statusMenu.anchorEl)}
        onClose={() => setStatusMenu({ anchorEl: null, row: null })}
      >
        {statusOptions.map((status) => {
          const statusColor = colorFinder(status.color);
          return (
            <MenuItem
              key={status.itemId}
              selected={statusMenu.row?.statusId === status.itemId}
              onClick={() => handleStatusChange(status.itemId)}
            >
              <Chip
                label={status.itemName}
                size="small"
                variant="outlined"
                sx={{
                  color: statusColor?.textColor,
                  borderColor: statusColor?.colorCode,
                  backgroundColor: statusColor?.kanbanChipBgColor,
                }}
              />
            </MenuItem>
          );
        })}
      </Menu>

      <WorksheetEmailDialog
        open={emailDialog.open}
        onClose={() => setEmailDialog((current) => ({ ...current, open: false }))}
        onSend={handleSendWorksheetEmail}
        config={config}
        user={user}
        emailTemplates={emailDialog.templates}
        initialEmailValues={emailDialog.initialEmailValues}
        fixedAttachments={emailDialog.fixedAttachments}
      />
    </Container>
  );
};

export default WorksheetReport;
